import React, { FC, Suspense, lazy } from "react";
import { Typography, CircularProgress, Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Lazy load ActionButton to prevent flickering when its dependencies change
const ActionButton = lazy(() =>
  import("./ActionButton").then((module) => ({ default: module.ActionButton })),
);

interface MessageContentProps {
  content: string;
  isUser: boolean;
}

/**
 * Preprocesses arrow syntax (→) and emoji-prefixed lines for proper markdown rendering.
 * This ensures proper spacing and list formatting instead of inline cramming.
 *
 * Pattern detection:
 * - Lines starting with "→ [Link]" are converted to "- [Link]" (markdown list)
 * - Lines starting with emoji (✅, ❌, ⚠️, etc.) get double line breaks for spacing
 * - Blank lines are added before/after blocks for proper separation
 *
 * Example:
 * Input:
 *   Some text
 *   → [Action 1](action:one) - Description
 *   → [Action 2](action:two) - Description
 *   ✅ **Feature 1** - Description
 *   ✅ **Feature 2** - Description
 *   More text
 *
 * Output:
 *   Some text
 *
 *   - [Action 1](action:one) - Description
 *   - [Action 2](action:two) - Description
 *
 *   ✅ **Feature 1** - Description
 *   ✅ **Feature 2** - Description
 *
 *   More text
 */
const preprocessArrowSyntax = (content: string): string => {
  const lines = content.split("\n");
  const result: string[] = [];
  let inArrowBlock = false;
  let inEmojiBlock = false;

  lines.forEach((line) => {
    // Match arrow lines: standalone (→ text), numbered (1. → text), or bullet (- → text)
    // Also handles arrows inside markdown formatting: 1. **→ [Link]**
    // The arrow can be followed by: [Link], **text**, *text*, or plain text
    const isArrowLine =
      /^(\d+\.\s*)?(-\s*)?(\*\*|\*|__)?→\s*(\[|\*{1,2}|__|[A-Za-z])/.test(line);

    // Match emoji-prefixed lines: ✅ text, ❌ text, ⚠️ text, etc.
    // Only matches emojis at START of line (^ anchor), safe for emojis inside text
    const isEmojiLine =
      /^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]\s+/u.test(line);

    if (isArrowLine && !inArrowBlock) {
      // Start of arrow block - add blank line before (unless at start of content)
      if (result.length > 0 && result[result.length - 1] !== "") {
        result.push("");
      }

      // Convert arrow line to proper list format
      let converted = line;
      if (line.match(/^\d+\.\s*(\*\*|\*|__)?→/)) {
        // Numbered list with arrow: "1. → [Link]" becomes "1. [Link]"
        converted = line.replace(
          /^(\d+\.)\s*(\*\*|\*|__)?\s*→\s*/,
          (_match, num, fmt) => {
            return fmt ? `${num} ${fmt}` : `${num} `;
          },
        );
      } else if (line.match(/^-\s*(\*\*|\*|__)?→/)) {
        // Bullet list with arrow: "- → [Link]" becomes "- [Link]"
        converted = line.replace(/^-\s*(\*\*|\*|__)?\s*→\s*/, (_match, fmt) => {
          return fmt ? `- ${fmt}` : "- ";
        });
      } else {
        // Standalone arrow: "→ [Link]" becomes "- [Link]"
        converted = line.replace(/^(\*\*|\*|__)?\s*→\s*/, (_match, fmt) => {
          return fmt ? `- ${fmt}` : "- ";
        });
      }

      result.push(converted);
      inArrowBlock = true;
    } else if (isArrowLine && inArrowBlock) {
      // Continue arrow block - same conversion logic
      let converted = line;
      if (line.match(/^\d+\.\s*(\*\*|\*|__)?→/)) {
        converted = line.replace(
          /^(\d+\.)\s*(\*\*|\*|__)?\s*→\s*/,
          (_match, num, fmt) => {
            return fmt ? `${num} ${fmt}` : `${num} `;
          },
        );
      } else if (line.match(/^-\s*(\*\*|\*|__)?→/)) {
        converted = line.replace(/^-\s*(\*\*|\*|__)?\s*→\s*/, (_match, fmt) => {
          return fmt ? `- ${fmt}` : "- ";
        });
      } else {
        converted = line.replace(/^(\*\*|\*|__)?\s*→\s*/, (_match, fmt) => {
          return fmt ? `- ${fmt}` : "- ";
        });
      }
      result.push(converted);
    } else if (isEmojiLine && !inEmojiBlock) {
      // Start of emoji block - add blank line before (unless at start)
      if (result.length > 0 && result[result.length - 1] !== "") {
        result.push("");
      }

      // Add double-space at end for line break (markdown syntax)
      result.push(`${line}  `);
      inEmojiBlock = true;
    } else if (isEmojiLine && inEmojiBlock) {
      // Continue emoji block - add double-space for line breaks
      result.push(`${line}  `);
    } else {
      if (inArrowBlock && !isArrowLine) {
        // End of arrow block - add blank line after (unless next is blank)
        if (line !== "") {
          result.push("");
        }
        inArrowBlock = false;
      }
      if (inEmojiBlock && !isEmojiLine) {
        // End of emoji block - add blank line after (unless next is blank)
        if (line !== "") {
          result.push("");
        }
        inEmojiBlock = false;
      }
      result.push(line);
    }
  });

  return result.join("\n");
};

export const MessageContent: FC<MessageContentProps> = ({
  content,
  isUser,
}) => {
  // User messages: render markdown with inherited (white) text color
  if (isUser) {
    return (
      <Box
        sx={{
          fontSize: "0.8rem",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          "& p": { m: 0, mb: 0.5 },
          "& p:last-child": { mb: 0 },
          "& ul, & ol": { m: 0, mb: 0.5, pl: 2 },
          "& ul:last-child, & ol:last-child": { mb: 0 },
          "& li": { mb: 0.25 },
          "& a": { color: "inherit", textDecoration: "underline" },
          "& code": {
            bgcolor: "rgba(255,255,255,0.15)",
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            fontSize: "0.875em",
          },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
      </Box>
    );
  }

  // Preprocess arrow syntax to convert → lines into markdown lists
  const preprocessed = preprocessArrowSyntax(content);

  // Additional cleanup: Remove arrows before markdown links (anywhere in text)
  // Handles cases like:
  // - "A **→ [Link]** is..." (bold around arrow+link)
  // - "In the Designer, → [Link]" (standalone arrow)
  // - "Use **→ [Link]** to..." (bold around arrow+link)
  // Strategy: Remove arrow symbol, use non-breaking space to prevent collapse
  const cleanedContent = preprocessed
    // Pattern 1: **→ [Link]** → **[Link]** (arrow inside bold, no space to keep bold valid)
    // No \s* between ** and → to avoid matching closing bold: "text** → [Link]"
    .replace(/\*\*→\s*(\[)/g, "**$1")
    // Pattern 2: *→ [Link]* → *[Link]* (arrow inside italic, no space to keep italic valid)
    .replace(/\*→\s*(\[)/g, "*$1")
    // Pattern 3: __→ [Link]__ → __[Link]__ (arrow inside underline bold, no space)
    .replace(/__→\s*(\[)/g, "__$1")
    // Pattern 4: Standalone → [Link] → [Link] (use nbsp to prevent whitespace collapse with React components)
    .replace(/\s*→\s*(\[)/g, "\u00A0$1");

  // Custom link component that detects semantic actions
  const LinkComponent = ({
    href,
    children,
  }: {
    href?: string;
    children?: React.ReactNode;
  }) => {
    // Check if this is a semantic action link
    if (
      href &&
      (href.startsWith("action:") ||
        href.startsWith("feature:") ||
        href.startsWith("step:") ||
        href.startsWith("modal:") ||
        href.startsWith("concierge:"))
    ) {
      // Extract text from children
      const label =
        typeof children === "string"
          ? children
          : React.Children.toArray(children).join("");

      return (
        <Suspense
          fallback={
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                ml: 0.5,
                "&:first-of-type": { ml: 0 }, // No margin when first element (e.g., inside **[link]**)
              }}
            >
              <CircularProgress size={12} />
              <Typography
                component="span"
                variant="body2"
                sx={{ fontSize: "inherit" }}
              >
                {label}
              </Typography>
            </Box>
          }
        >
          <Box component="span" sx={{ ml: 0.5, "&:first-of-type": { ml: 0 } }}>
            <ActionButton label={label} target={href} />
          </Box>
        </Suspense>
      );
    }

    // Regular external/internal link
    if (href?.startsWith("http")) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }

    return <a href={href}>{children}</a>;
  };

  return (
    <Box
      sx={{
        fontSize: "0.875rem",
        "& p": { m: 0, mb: 1 },
        "& p:last-child": { mb: 0 },
        "& ul, & ol": { m: 0, mb: 1, pl: 2 },
        "& ul:last-child, & ol:last-child": { mb: 0 },
        "& li": { mb: 0.5 },
        "& pre": {
          bgcolor: "grey.100",
          p: 1,
          borderRadius: 1,
          overflow: "auto",
          mb: 1,
        },
        "& pre:last-child": { mb: 0 },
        "& code": {
          bgcolor: "grey.100",
          px: 0.5,
          py: 0.25,
          borderRadius: 0.5,
          fontSize: "0.875em",
        },
        "& pre code": {
          bgcolor: "transparent",
          px: 0,
          py: 0,
        },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          m: 0,
          mb: 1,
          fontWeight: 600,
        },
        "& blockquote": {
          borderLeft: "3px solid",
          borderColor: "grey.300",
          pl: 2,
          m: 0,
          mb: 1,
        },
        "& a": {
          color: "primary.main",
          textDecoration: "underline",
        },
        "& img": {
          maxWidth: "100%",
          height: "auto",
          borderRadius: 1,
          my: 1,
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: LinkComponent,
        }}
        urlTransform={(url) => url} // Disable URL sanitization to allow custom schemes
      >
        {cleanedContent}
      </ReactMarkdown>
    </Box>
  );
};
